import image from "../../assets/blog/virtual-cam.jpg";
import "./Blog.scss";
import BlogLink from "./BlogLink";
import CodeBlock from "./CodeBlock";
import { Spacer } from "./Spacer";

export function VirtualCamera() {
  return (
    <div className="blog-post">
      <div className="title">Windows virtual camera with javascript & ffi</div>
      <div className="paragraph">
        Have you ever wondered how can we build a virtual web camera for Windows
        using javascript instead of a language like c# or c++ which gives you
        direct access to windows native API's. That's when foreign function
        interfaces also known as FFI comes into play. In this article let's see
        how we can utilize a dll file to communicate with Windows{" "}
        <BlogLink>DirectShow API</BlogLink> using javascript and nodejs, and as
        a bonus how to feed an image rendered on a html canvas to our virtual
        web camera.
      </div>
      <Spacer />
      <img src={image} className="blog-image" />
      <Spacer />
      <div className="paragraph">
        First of all, what is a Foreign Function Interface or commonly known as
        FFI? FFI is a programming mechanism that allows code written in one
        language to call functions or use services written in another language,
        in this case invoking functions of a dll binary file using javascript.
        For this we are going to utilize a library called{" "}
        <BlogLink href="https://www.npmjs.com/package/koffi">koffi</BlogLink>,
        and this allows us to invoke the functions in the dll file that are
        written in c language.
      </div>
      <Spacer />
      <div className="paragraph">
        Then we have to decide which dll file are we exactly using for this
        purpose? I found this amazing library called{" "}
        <BlogLink href="https://github.com/tshino/softcam">softcam</BlogLink> on
        github, which is a visual studio project. You can clone this repository,
        open with visual studio and build the dll files following the Readme
        file. This dll gives us access to communicate with windows Direct Show
        API using four simple functions.{" "}
        <span className="important">
          You have to install this dll file in order to below code to work.
          Otherwise windows does not recognize it.
        </span>
      </div>
      <Spacer />
      <ol>
        <li>scCreateCamera</li>
        <li>scWaitForConnection</li>
        <li>scSendFrame</li>
        <li>scDeleteCamera</li>
      </ol>
      <Spacer />
      <div className="paragraph">
        You can refer{" "}
        <BlogLink href="https://github.com/tshino/softcam/blob/main/src/softcam/softcam.h">
          this
        </BlogLink>{" "}
        softcam.h header file to understand the parameters of these 4 functions.
      </div>
      <div className="separater">...</div>
      <div className="paragraph">
        Now let's see how the actual javascript code is written. First
        initialize an empty node js project with <code>npm init -y</code>{" "}
        command. Then install the koffi library using <code>npm i koffi</code>{" "}
        command. I'm using commonjs imports and exports in this example, you can
        use es6 imports if you prefer that. Lets create a file called softcam.js
        in the project root and import the koffi library. Also paste the
        softcam.dll file that we compiled from the visual studio project in the
        root directory. And then we have to define each function that we are
        going to utilize from the dll file using the koffi library after loading
        the softcam.dll file.
      </div>
      <Spacer />
      <CodeBlock>
        {`const lib = koffi.load(dllPath);

const scCreateCamera = lib.stdcall("scCreateCamera", "void*", ["int", "int", "float"]);

const scWaitForConnection = lib.stdcall("scWaitForConnection", "bool", ["void*", "float"]);

const scSendFrame = lib.stdcall("scSendFrame", "void", ["void*", "void*"]);

const scDeleteCamera = lib.stdcall("scDeleteCamera", "bool", ["void*"]);`}
      </CodeBlock>
      <Spacer />
      <div className="paragraph">
        Now let's wrap above code in a function called softcam and define helper
        functions to utilize the above defined functions.
      </div>
      <Spacer />
      <CodeBlock>
        {`const koffi = require("koffi");
const path = require("path");

const dllPath = path.join(__dirname, "softcam.dll");

function softCam(width = 320, height = 240, frameRate = 30) {
  const lib = koffi.load(dllPath);

  const scCreateCamera = lib.stdcall("scCreateCamera", "void*", [
    "int",
    "int",
    "float",
  ]);

  const scWaitForConnection = lib.stdcall("scWaitForConnection", "bool", [
    "void*",
    "float",
  ]);

  const scSendFrame = lib.stdcall("scSendFrame", "void", ["void*", "void*"]);

  const scDeleteCamera = lib.stdcall("scDeleteCamera", "bool", ["void*"]);

  const camera = scCreateCamera(width, height, frameRate);

  function waitForConnection() {
    scWaitForConnection(camera, 5.0);
  }

  function sendFrame(imgData) {
    scSendFrame(camera, imgData);
  }

  function deleteCamera() {
    scDeleteCamera(camera);
  }

  return {
    sendFrame,
    waitForConnection,
    deleteCamera,
  };
}

module.exports = softCam;
`}
      </CodeBlock>
      <Spacer />
      <div className="paragraph">
        Here we have defined some additional parameters: width, height and
        framerate for our virtual camera. Now let's see how we can utilize this
        function that we wrote. Create another file called index.js in the root
        directory and import the softcam function that we wrote.
      </div>
      <Spacer />
      <CodeBlock>{`const Camera = require("./softcam/softcam");

const camera = Camera();

camera.waitForConnection();

// This array should contain individual pixel RGB values in BGR order
// Example: [B, G, R, B, G, R, B, G, R, ...]
const imageData = new Uint8Array([]);

camera.sendFrame(imageData);`}</CodeBlock>
      <Spacer />
      <div className="paragraph">
        You can feed the image/video data to the virtual camera using the{" "}
        <code>sendFrame</code> function. This function takes an array of pixel
        values in BGR order.
      </div>
      <div className="separater">...</div>
      <div className="paragraph">
        You can use a canvas element to get the image data and pass it to the
        virtual camera. Below is an example of how you can get the image data
        from a canvas element. First create a html file with a canvas element in
        it with the id of canvas. Then create a script tag and paste the below
        code.
      </div>
      <Spacer />
      <CodeBlock>
        {`function formatImageData(uint8Array) {
  const arr = [];
  for (let i = 0; i < uint8Array.length; i += 4) {
    arr.push(uint8Array[i + 2], uint8Array[i + 1], uint8Array[i]);
  }
  return new Uint8Array(arr);
}

const canvas = document.querySelector("#canvas");
const context = canvas.getContext("2d", {
  alpha: false,
  willReadFrequently: true,
});
const imageData = context.getImageData(0, 0, width, height);
const formattedData = formatImageData(imageData.data);`}
      </CodeBlock>
      <Spacer />
      <div className="paragraph">
        Now all you got to do is create a simple express server in the index.js
        file, and POST this <code>formattedData</code> to a endpoint and call
        the <code>sendFrame</code> function with the data.
      </div>
      <div className="ending">
        Huraay!! Now you got a working a virtual camera. I only covered the
        basics with this article, and you can experiment with this to do more
        cool things like utilizing websockets or webrtc to stream video feeds
        and many more things.
      </div>
    </div>
  );
}
